/* David Pladen — maximum number of items per order ("DP cart drawer" →
   Order limit). Every cart request from the theme (add, change, update) goes
   through fetch, so this checks the resulting item count before the request is
   sent. Going over the maximum returns a 422 with the message, which the cart
   drawer and the product page show as their normal error. Lowering a quantity
   is always allowed. Variants in config.skip (e.g. accessories) do not count.
   Config: window.DPCartLimit = { max, message, skip }. */
(() => {
  const config = window.DPCartLimit;
  if (!config || !(config.max > 0) || window.fetch.dpLimited) return;

  const nativeFetch = window.fetch.bind(window);
  const message = (config.message || '').replace('[max]', config.max);
  const skip = new Set((config.skip || []).map(String));
  const counts = (variantId) => !skip.has(String(variantId));
  const variantOf = (ref) => String(ref).split(':')[0];

  const pathOf = (input) => {
    try {
      const url = typeof input === 'string' ? input : input.url;
      return new URL(url, window.location.origin).pathname.replace(/\.js$/, '');
    } catch (error) {
      return '';
    }
  };
  const routes = () => ({
    add: (window.routes?.cart_add_url || '/cart/add').replace(/\.js$/, ''),
    change: (window.routes?.cart_change_url || '/cart/change').replace(/\.js$/, ''),
    update: (window.routes?.cart_update_url || '/cart/update').replace(/\.js$/, ''),
  });

  /* The request body as a plain object (JSON or FormData) */
  const readBody = (body) => {
    if (!body) return {};
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (error) {
        return Object.fromEntries(new URLSearchParams(body));
      }
    }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const out = {};
      body.forEach((value, key) => {
        if (typeof value === 'string') out[key] = value;
      });
      return out;
    }
    return {};
  };

  const qty = (value, fallback = 1) => {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? fallback : n;
  };

  /* Lines a change/update key points at: a line key, a variant id or a 1-based line number */
  const linesOf = (cart, ref, byLine) => {
    if (byLine) return cart.items[ref - 1] ? [cart.items[ref - 1]] : [];
    const key = String(ref);
    if (key.includes(':')) return cart.items.filter((item) => item.key === key);
    return cart.items.filter((item) => String(item.variant_id) === key);
  };
  const counted = (lines) => lines.filter((item) => counts(item.variant_id)).reduce((sum, item) => sum + item.quantity, 0);
  /* Change in counted items when the line(s) behind ref get quantity value */
  const delta = (cart, ref, byLine, value) => {
    const lines = linesOf(cart, ref, byLine);
    const id = lines[0]?.variant_id ?? (byLine ? null : variantOf(ref));
    if (id === null || !counts(id)) return 0;
    return qty(value, 0) - counted(lines);
  };

  /* Item count after the request, or null when the request does not change quantities */
  const countAfter = (kind, data, cart) => {
    const now = counted(cart.items);
    if (kind === 'add') {
      const items = Array.isArray(data.items) ? data.items : data.id ? [data] : [];
      return now + items.filter((item) => counts(item.id)).reduce((sum, item) => sum + qty(item.quantity), 0);
    }
    if (kind === 'change') {
      if (data.quantity === undefined) return null;
      const byLine = data.line !== undefined;
      return now + delta(cart, byLine ? qty(data.line, 0) : data.id, byLine, data.quantity);
    }
    const updates = data.updates;
    if (!updates) return null;
    let total = now;
    if (Array.isArray(updates)) {
      updates.forEach((value, index) => {
        total += delta(cart, index + 1, true, value);
      });
    } else {
      Object.entries(updates).forEach(([ref, value]) => {
        total += delta(cart, ref, false, value);
      });
    }
    return total;
  };

  const blocked = () => {
    config.lastBlocked = Date.now();
    return new Response(JSON.stringify({ status: 422, message, description: message }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  };

  const limitedFetch = async (input, init = {}) => {
    const method = (init.method || (typeof input !== 'string' && input.method) || 'GET').toUpperCase();
    const path = pathOf(input);
    const r = routes();
    const kind = method !== 'POST' ? null : path === r.add ? 'add' : path === r.change ? 'change' : path === r.update ? 'update' : null;
    if (!kind) return nativeFetch(input, init);

    try {
      const data = readBody(init.body);
      const cart = await nativeFetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((res) => res.json());
      const after = countAfter(kind, data, cart);
      if (after !== null && after > config.max && after > counted(cart.items)) return blocked();
    } catch (error) {
      // When the check itself fails, let Shopify handle the request as usual
    }
    return nativeFetch(input, init);
  };
  limitedFetch.dpLimited = true;
  window.fetch = limitedFetch;

  /* The product page shows "description" from DPCart.add's result; the drawer
     returns null on an error, so hand the message back in that case */
  document.addEventListener('DOMContentLoaded', () => {
    const cart = window.DPCart;
    if (!cart || cart.add.dpLimited) return;
    const add = cart.add;
    cart.add = async function limitedAdd(items, trigger) {
      const started = Date.now();
      const result = await add.call(this, items, trigger);
      if (!result && config.lastBlocked >= started) return { status: 422, description: message };
      return result;
    };
    cart.add.dpLimited = true;
  });
})();
